export default {
  purchasing: {
    title: 'Purchasing Process',
    steps: [
      {
        number: '01',
        title: 'Clarification of your Order Specifications',
        description:
          'Begin by clarifying the chemicals you wish to purchase, their required specifications, your monthly usage (ie. the estimated quantity required for your project), the price you wish to pay, the delivery schedule you wish us to fulfil, etc. Additionally, please ensure you have the necessary licenses/permits which are required to import your intended purchase into your country.',
        expandedTitle: 'Why Buy Chemicals Manufactured in Japan?',
        expandedParagraphs: [
          'When taking only price and specifications into consideration, it may seem that Japanese chemical products are of high cost. For this reason, these products are not always the best option for some users, depending on their purposes and needs.',
          'However, Daishin Corporation offers only genuine products and, from the very start, you will appreciate their high functionality, uniform quality, unique technologies and ease of use, as well as the user-orientated service that is commensurate with this higher cost.',
          'Our aim is to make the world a less complicated, more transparent and more prosperous place by supplying quality Japanese chemical products to the users who need them.',
        ],
      },
      {
        number: '02',
        title: 'Consultation',
        description:
          'Once you have finalised your order, contact us. We will review your request and make a proposal based on your requirements. To ensure that we can propose the most suitable solution for your order, please supply us with information regarding your situation (such as the nature of your project, the intended application of the product you are ordering, your final product, etc.) and the purpose of contacting us for a quote (for example, cost reduction, quality improvement, a dual vendor strategy, etc.)',
        expandedTitle: 'How to Choose a Foreign-Based Chemical Supplier',
        comparisonLocal: {
          title: 'The Pros of a Local Specialized Chemical Trading Company',
          items: [
            'Strong ties with chemical manufacturers due to specialization in the chemical industry.',
            'Continuity in who will be managing your business, as there will be few personnel changes.',
            'Due to specialization in the chemical industry, requests can be responded to quickly.',
            'Strong individual authority means flexibility according to client needs.',
          ],
        },
        comparisonGlobalCons: {
          title: 'The Cons of Global General Trading Companies',
          items: [
            'A lack of specialization in the subject results in both a variable quality of their knowledge and skill set.',
            'Regularity in personnel changes means that your point of contact will often change.',
            'A focus on an array of areas means client requests cannot be quickly responded to.',
            "Prioritization of company policy and guidelines, comes at the expense of the client's needs.",
          ],
        },
        comparisonGlobalPros: {
          title: 'The Pros of Global General Trading Companies',
          items: [
            'Name recognition can provide a sense of security from the very start of negotiations.',
            'The variety of knowledge within the company means that a wide variety of products - from cup noodles to rockets - can be efficiently handled.',
            'A large scale operation with the capacity to deal with projects worth hundreds of millions of dollars.',
            'Possess the ability to handle non-product aspects, such as resource development, M&A, and so on.',
            'Can provide products from branch offices from abroad, even by non-Japanese manufacturers.',
          ],
        },
        comparisonLocalCons: {
          title: 'The Cons of a Local Specialized Chemical Trading Company',
          items: [
            'The lack of name recognition may initially provide a sense of insecurity in the client.',
            'Due to a tight focus on the chemical industry, there may be unfamiliarity with information outside of core concerns.',
            'A smaller scale operation which is not in a position to deal with such large scale projects.',
            'Have an inability to handle certain aspects outside of their specialization.',
            'Are unable to provide products from foreign-based branch offices if the manufacturer is not Japan-based.',
          ],
        },
      },
      {
        number: '03',
        title: 'Discussions',
        description:
          'Our product provision is based on CIF (Cost, Insurance, and Freight) or CFR (Cost and Freight) standards. At this point in the process, we request that you confirm the import duty for your country via the HS code. Additionally, check the total cost of the import procedure with a forwarding agent and calculate the final cost that the transaction will incur for your company.',
        linkText: 'Learn about Import/Export Terms',
        linkPath: '/import-export-terms',
      },
      {
        number: '04',
        title: 'Sampling Test',
        description:
          'Once the cost of your purchase, based on the specification sheet and our offer, has been finalized and accepted, you will have the opportunity to check the quality of the product through a sampling test. Typically we will provide you with a sample between 50g and 1kg, though sample availability is dependent on products and situations.',
      },
      {
        number: '05',
        title: 'Trial Order',
        description:
          "When the sample test proves successful we recommend that, for safety's sake, you first purchase the smallest available unit of the product (such as a 200kg drum) before making a bulk order. By doing so, you can conduct a final quality check by using the product in the actual circumstances you intend to use it in.",
      },
      {
        number: '06',
        title: 'Initial Contract',
        description:
          'If, upon finishing your trial order, you wish to commit to a larger purchase, send confirmation of your order to us by email and we will draw up a sales contract, upon receipt of which you will make your first contracted payment. Our contract options include both short term options, such as a contract for a single shipment, or longer term, annual contracts. We recommend you to start with a short-term contract and, once you are secure in the quality of our service and our products, move up to a longer term deal. Once a contract between both parties has been finalized, it is not possible for it to be cancelled. Please confirm the status of import control and any import applications for the product you are intending to purchase prior to submitting your order confirmation.',
      },
      {
        number: '07',
        title: 'Initial Shipment',
        description:
          'The product is shipped according to contract conditions and, upon completion of import procedures, will be delivered to your site by your forwarding agent.',
        subSteps: [
          {
            title: 'Import Clearance Arrangements',
            description:
              'Your appointed forwarding agent will arrange and prepare for import clearance. They will identify the product\'s declared value and applicable classification code (H.S. Code) based on both the shipping documents we provide and additional information obtained from you before submitting an import declaration to customs. In cases where the cargo requires any special permissions, due to any laws or regulations other than import clearance, the necessary applications should be prepared in advance to be submitted to the relevant government office at this time.',
          },
          {
            title: 'Import Declaration',
            description:
              'Once your product has been delivered to the selected port, your forwarding agent will declare your purchase to customs, at which time customs officials will conduct document reviews and cargo inspections in accordance with local regulations. Following completion of these inspections, any additional applications that may be required need to be dealt with accordingly.',
          },
          {
            title: 'Import Permit',
            description:
              'Once reviews and inspections have been completed, and all relevant import duties and VAT have been paid, an import permit will be issued. In cases in which additional applications have to be made based on other regulations, an import permit will not be issued until these are approved.',
          },
          {
            title: 'Delivery Order Exchange',
            description:
              'Your cargo will be delivered once a Delivery Order (D/O) has been issued by the shipping company. You pay all local charges to the shipping company and exchange the original Bill of Lading (B/L) with the D/O.',
          },
          {
            title: 'Delivery Arrangement',
            description:
              'The cargo is delivered to your site by either container drayage or cargo truck. When receiving the cargo at the shipping company yard (CY) or container freight station (CFS), the D/O and the official import permit to the CY/CFS must be submitted. The CY/CFS will complete delivery after confirming the D/O and the details of the letter of permit.',
          },
        ],
        closingNote: 'This procedure may vary by country, so please check your local regulations in advance.',
        linkText: 'View Chemical Trade Information',
        linkPath: '/chemical-trade-info',
      },
      {
        number: '08',
        title: 'Feedback',
        description:
          'We would appreciate any feedback on the quality of our products and services in order to help us improve any and all future shipments.',
      },
    ],
    cta: {
      title: 'Ready to Get Started?',
      description: 'Contact us today to begin your chemical procurement journey with Daishin Corporation.',
      btn: 'Contact Us Now',
    },
  },
};
